import { PaymentMethod, PaymentMethodType } from 'types';

const cents = (value: number) => Math.round(value * 100);

/** The first partial approval retains the original tip selection for this sale. */
export function getPartialPaymentTip(paymentMethods: PaymentMethod[]) {
  const cards = paymentMethods
    .filter((method) => method.cloverPaymentId)
    .sort(
      (a, b) =>
        (Date.parse(a.cloverCreatedTime ?? '') || 0) -
        (Date.parse(b.cloverCreatedTime ?? '') || 0)
    );
  const firstPartial = cards.findIndex(
    (method) => method.cloverAllocation?.policy === 'PURCHASE_FIRST'
  );
  if (firstPartial < 0) return null;
  const root = cards[firstPartial];
  const collectedTip = cards.reduce(
    (sum, method) => sum + (method.cloverTipAmount ?? 0),
    0
  );
  const earlierTip = cards
    .slice(0, firstPartial)
    .reduce((sum, method) => sum + (method.cloverTipAmount ?? 0), 0);
  return {
    rootPaymentId: root.cloverPaymentId!,
    desiredTip: Math.max(
      collectedTip,
      earlierTip + root.cloverAllocation!.requestedTip
    ),
    collectedTip,
  };
}

/** All returned amounts are cents. The chosen tip remains due exactly once. */
export function getPartialPaymentBalance({
  paymentMethods,
  saleTotal,
  currentTip = 0,
}: {
  paymentMethods: PaymentMethod[];
  saleTotal: number;
  currentTip?: number;
}) {
  const selection = getPartialPaymentTip(paymentMethods);
  if (!selection) return null;
  const { desiredTip, collectedTip: allCollectedTip } = selection;
  const paid = paymentMethods.reduce(
    (sum, method) =>
      sum +
      cents(method.amount) +
      (method.type === PaymentMethodType.Credit
        ? cents(method.proccessingFee ?? 0)
        : 0),
    0
  );
  const requiredTotal = cents(saleTotal) - cents(currentTip);
  const totalWithChosenTip = requiredTotal + desiredTip;
  const remaining = totalWithChosenTip - paid;
  // Cash/check contribute to the purchase first. Only already-collected card
  // tips are excluded from the funds available to pay the purchase.
  const requiredRemaining = Math.max(
    0,
    requiredTotal - (paid - allCollectedTip)
  );
  const tipRemaining = Math.max(0, remaining - requiredRemaining);
  return {
    ...selection,
    paid,
    desiredTip,
    collectedTip: allCollectedTip,
    requiredRemaining,
    tipRemaining,
    remaining,
    overpaid: remaining < 0,
  };
}
